import React, { useState } from 'react';
import { FaBed, FaHeartbeat, FaUserMd, FaChartLine } from 'react-icons/fa';
import { useTheme } from '@theme/useTheme';

interface CardData {
  id: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  expandedContent?: string;
}

interface ExpandableCardsProps {
  cards: CardData[];
}

const ExpandableCards: React.FC<ExpandableCardsProps> = ({ cards }) => {
  const [expandedCard, setExpandedCard] = useState<CardData | null>(null);
  const [showModal, setShowModal] = useState(false);
  const { colors } = useTheme();

  const expandCard = (card: CardData) => {
    setExpandedCard(card);
    setShowModal(true);
  };

  const collapseCard = () => {
    setShowModal(false);
    setTimeout(() => {
      setExpandedCard(null);
    }, 300);
  };

  const renderCard = (card: CardData) => {
    return (
      <div
        key={card.id}
        className="w-full max-w-sm mx-auto p-6 border-2 cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
        style={{
          backgroundColor: colors.background,
          borderColor: colors.primary,
          borderTopRightRadius: '2rem',
          borderBottomLeftRadius: '2rem'
        }}
        onClick={() => expandCard(card)}
      >
        <div className="flex flex-col items-center justify-center h-full text-center">
          <div className="mb-4 p-3 rounded-full" style={{ backgroundColor: colors.primary }}>
            {card.icon}
          </div>
          <h3
            className="text-lg font-semibold leading-6"
            style={{ color: colors.heading }}
          >
            {card.title}
          </h3>
        </div>
      </div>
    );
  };

  const renderExpandedCard = () => {
    if (!expandedCard) return null;

    return (
      <div
        className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-300 ${showModal ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        onClick={collapseCard}
      >
        <div
          className={`w-full max-w-5xl max-h-[98vh] p-8 border shadow-2xl transform transition-all duration-300 ${showModal ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
            }`}
          style={{
            backgroundColor: colors.background,
            borderColor: colors.primary,
            borderTopRightRadius: '3rem',
            borderBottomLeftRadius: '3rem'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header with icon */}
          <div className="flex items-center mb-4">
            <div className="p-3 rounded-full mr-3" style={{ backgroundColor: colors.primary }}>
              {expandedCard.icon}
            </div>
            <h2 className="text-2xl font-bold leading-8" style={{ color: colors.heading }}>
              {expandedCard.title}
            </h2>
          </div>

          {/* Content */}
          <div className="max-h-[60vh] overflow-y-auto mb-8">
            <p className="text-lg leading-7 mb-8 font-normal" style={{ color: colors.text }}>
              {expandedCard.expandedContent || expandedCard.description}
            </p>

            <div className="mb-8">
              <h3 className="text-2xl font-semibold mb-6" style={{ color: colors.heading }}>Key Points:</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="w-2 h-2 rounded-full mr-4 mt-2 flex-shrink-0" style={{ backgroundColor: colors.primary }}></div>
                  <span className="text-base leading-6" style={{ color: colors.text }}>
                    Comprehensive understanding of the condition
                  </span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 rounded-full mr-4 mt-2 flex-shrink-0" style={{ backgroundColor: colors.primary }}></div>
                  <span className="text-base leading-6" style={{ color: colors.text }}>
                    Evidence-based treatment approaches
                  </span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 rounded-full mr-4 mt-2 flex-shrink-0" style={{ backgroundColor: colors.primary }}></div>
                  <span className="text-base leading-6" style={{ color: colors.text }}>
                    Latest research and medical insights
                  </span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 rounded-full mr-4 mt-2 flex-shrink-0" style={{ backgroundColor: colors.primary }}></div>
                  <span className="text-base leading-6" style={{ color: colors.text }}>
                    Practical tips for daily management
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Close button */}
          <div className="flex justify-end">
            <button
              onClick={collapseCard}
              className="py-3 px-6 rounded-lg font-semibold text-white text-base transition-all duration-200 hover:opacity-90 hover:shadow-lg"
              style={{ backgroundColor: colors.primaryButton }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full mt-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 justify-items-center">
        {cards.map((card) => renderCard(card))}
      </div>
      {renderExpandedCard()}
    </div>
  );
};

// Default data for sleep apnea cards
const defaultCards: CardData[] = [
  {
    id: "1",
    icon: <FaBed size={48} color="white" />,
    title: "What is Sleep Apnea?",
    description:
      "A serious sleep disorder where breathing repeatedly stops and starts during sleep. It affects millions of people worldwide, often going undiagnosed.",
    expandedContent: `Sleep apnea is a potentially serious sleep disorder in which breathing repeatedly stops and starts during sleep. If you snore loudly and feel tired even after a full night's sleep, you might have sleep apnea.

The main types of sleep apnea are:

Obstructive Sleep Apnea (OSA): The more common form that occurs when throat muscles relax and block the airway during sleep.

Central Sleep Apnea: Occurs when your brain doesn't send proper signals to the muscles that control breathing.

Complex Sleep Apnea Syndrome: Also known as treatment-emergent central sleep apnea, occurs when someone has both obstructive sleep apnea and central sleep apnea.

Sleep apnea can affect anyone at any age, even children. However, certain factors increase your risk, including excess weight, neck circumference, narrowed airway, being male, being older, family history, use of alcohol or sedatives, smoking, and nasal congestion.`,
  },
  {
    id: "2",
    icon: <FaHeartbeat size={48} color="white" />,
    title: "Health Impact",
    description:
      "Untreated sleep apnea can lead to serious health complications including high blood pressure, heart disease, stroke, and diabetes.",
    expandedContent: `Untreated sleep apnea can have serious health consequences that extend far beyond poor sleep quality. The repeated interruptions in breathing during sleep can lead to significant strain on your cardiovascular system and other bodily functions.

Cardiovascular Complications:
- High blood pressure (hypertension)
- Heart disease and irregular heartbeats
- Increased risk of stroke
- Heart failure

Metabolic Effects:
- Type 2 diabetes and insulin resistance
- Weight gain and difficulty losing weight
- Metabolic syndrome

Mental Health and Cognitive Impact:
- Depression and anxiety
- Memory problems and difficulty concentrating
- Increased risk of accidents due to daytime fatigue
- Mood swings and irritability

Other Health Risks:
- Weakened immune system
- Gastroesophageal reflux disease (GERD)
- Complications with medications and surgery
- Liver problems

The good news is that with proper treatment, many of these health risks can be significantly reduced or even reversed.`,
  },
  {
    id: "3",
    icon: <FaUserMd size={48} color="white" />,
    title: "Treatment Options",
    description:
      "Various effective treatments are available, from lifestyle changes to CPAP therapy, depending on the severity of your condition.",
    expandedContent: `There are several effective treatment options for sleep apnea, ranging from lifestyle modifications to medical devices and surgical procedures. The best treatment depends on the type and severity of your sleep apnea, as well as your individual circumstances.

Lifestyle Changes:
- Weight loss if you're overweight
- Regular exercise
- Avoiding alcohol and sedatives
- Sleeping on your side instead of your back
- Quitting smoking
- Treating nasal congestion

CPAP Therapy:
Continuous Positive Airway Pressure (CPAP) is the most common and effective treatment for moderate to severe obstructive sleep apnea. It involves wearing a mask connected to a machine that delivers pressurized air to keep your airways open.

Other Breathing Devices:
- BiPAP (Bilevel Positive Airway Pressure)
- Adaptive servo-ventilation (ASV)
- Oral appliances and mouthpieces

Surgical Options:
- Tissue removal (uvulopalatopharyngoplasty)
- Jaw repositioning
- Implants
- Nerve stimulation
- Creating a new air passageway (tracheostomy)

Your healthcare provider will work with you to determine the most appropriate treatment plan based on your specific needs and preferences.`,
  },
  {
    id: "4",
    icon: <FaChartLine size={48} color="white" />,
    title: "Prevalence",
    description:
      "Approximately 1 in 5 adults have at least mild sleep apnea, and 1 in 15 have moderate to severe sleep apnea.",
    expandedContent: `Sleep apnea is more common than many people realize, affecting millions of individuals worldwide. Understanding the prevalence can help highlight the importance of awareness and proper diagnosis.

Global Statistics:
- Approximately 1 billion people worldwide have obstructive sleep apnea
- In the United States, about 1 in 5 adults have at least mild sleep apnea
- 1 in 15 adults have moderate to severe sleep apnea
- Up to 80% of cases remain undiagnosed

Demographics:
- More common in men than women (2:1 ratio)
- Risk increases with age, especially after 40
- Post-menopausal women have increased risk
- More prevalent in certain ethnic groups

Risk Factors:
- Obesity (BMI >30) significantly increases risk
- Neck circumference >17 inches (men) or >16 inches (women)
- Family history of sleep apnea
- Anatomical factors (large tonsils, deviated septum)

Economic Impact:
- Estimated annual cost of $15.9 billion in the US alone
- Increased healthcare utilization
- Lost productivity due to daytime sleepiness
- Higher accident rates

The high prevalence of undiagnosed sleep apnea makes screening and awareness campaigns crucial for public health. If you suspect you might have sleep apnea, consult with a healthcare provider for proper evaluation.`,
  },
];

export default ExpandableCards;
export { defaultCards };
